import { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Menu, Info, MoreHorizontal, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { FEATURE_FLAGS } from '../feature-flags';
import { useSidebar } from '../../../context/SidebarContext';
import { FavoriteButton } from '../../../components/FavoriteButton';
import { FiasAIToggle, useFiasAIContext } from '@/components/fias-ai';
import {
  BarChartIcon,
  DashboardIcon,
  PenToolIcon,
  RssFeedIcon,
  SettingsIcon,
  SparkleIcon,
  UserSquareIcon,
} from './Icons';
import { createPortal } from 'react-dom';
import { useRef } from 'react';

const ARCHE_ID = 'arche_social_media';
const ARCHE_DESCRIPTION =
  'AI-powered social media management — connect accounts, compose once, publish everywhere';

export function SocialMediaHeader() {
  const { isCollapsed, toggle } = useSidebar();
  const fiasAI = useFiasAIContext();
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <>
      <div className="flex-shrink-0 bg-social-bg backdrop-blur-sm z-[9999]">
        <div className="pl-4 lg:pr-0 pr-4 py-3 flex items-center justify-between gap-4">
          {/* Left: title + utility icons */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={toggle}
              className={`h-7 lg:h-11 w-7 lg:w-11 flex-shrink-0 bg-gray-100 dark:bg-zinc-700 ${isCollapsed ? '' : 'hidden'}`}
              title="Open sidebar"
            >
              <Menu className="h-6 w-6" />
            </Button>
            <h1 className="text-lg font-semibold !font-satoshi">Social Media</h1>

            {/* Info icon with description tooltip */}
            <div className="relative group">
              <Link
                to={`/marketplace/arches/${ARCHE_ID}`}
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="View Social Media arche details"
              >
                <Info className="h-3 w-3" />
              </Link>
              <div
                className="pointer-events-none absolute top-full left-0 mt-2 w-72 rounded-md border border-border bg-popover p-3 shadow-md opacity-0 group-hover:opacity-100 transition-opacity z-50"
                role="tooltip"
              >
                <p className="text-sm font-medium text-popover-foreground">Social Media</p>
                <p className="mt-1 text-xs text-muted-foreground">{ARCHE_DESCRIPTION}</p>
              </div>
            </div>

            <FavoriteButton archeId={ARCHE_ID} />
            <FiasAIToggle onClick={fiasAI.togglePanel} isActive={fiasAI.isPanelOpen} />
          </div>

          {/* Right: desktop icon-only nav / mobile three-dot trigger */}
          <div className="hidden sm:flex bg-background shadow-social-medium rounded-l-xl pl-5 pr-4 py-2">
            <SocialMediaNav />
          </div>

          <button
            className="sm:hidden flex items-center justify-center h-9 w-9 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Open navigation menu"
            onClick={() => setDrawerOpen(true)}
          >
            <MoreHorizontal className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <MobileNavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </>
  );
}

interface NavItem {
  to: string;
  icon: React.ReactNode;
  label: string;
  end?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  {
    to: '/a/arche_social_media/',
    icon: <PenToolIcon className="!h-4 !w-4" />,
    label: 'Compose',
    end: true,
  },
  {
    to: '/a/arche_social_media/overview',
    icon: <DashboardIcon className="!h-4 !w-4" />,
    label: 'Overview',
    end: true,
  },
  {
    to: '/a/arche_social_media/write',
    icon: <SparkleIcon className="!h-4 !w-4" />,
    label: 'Write',
  },
  {
    to: '/a/arche_social_media/inspiration',
    icon: <RssFeedIcon className="!h-4 !w-4" />,
    label: 'Inspiration',
  },
  {
    to: '/a/arche_social_media/accounts',
    icon: <UserSquareIcon className="!h-4 !w-4" />,
    label: 'Accounts',
  },
  ...(FEATURE_FLAGS.socialMediaAnalytics
    ? [
        {
          to: '/a/arche_social_media/analytics',
          icon: <BarChartIcon className="!h-4 !w-4" />,
          label: 'Analytics',
        },
      ]
    : []),
  {
    to: '/a/arche_social_media/settings',
    icon: <SettingsIcon className="!h-4 !w-4" />,
    label: 'Settings',
  },
];

function SocialMediaNav() {
  const [tooltip, setTooltip] = useState<{
    label: string;
    rect: DOMRect | null;
  }>({ label: '', rect: null });

  return (
    <>
      <nav className="flex items-center gap-4" aria-label="Social Media navigation">
        {NAV_ITEMS.map(({ to, icon: Icon, label, end }) => (
          <NavItem key={to} to={to} icon={Icon} label={label} end={end} setTooltip={setTooltip} />
        ))}
      </nav>

      {tooltip.rect &&
        createPortal(<Tooltip label={tooltip.label} rect={tooltip.rect} />, document.body)}
    </>
  );
}

function NavItem({
  to,
  icon: Icon,
  label,
  end,
  setTooltip,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  end?: boolean;
  setTooltip: React.Dispatch<React.SetStateAction<{ label: string; rect: DOMRect | null }>>;
}) {
  const ref = useRef<HTMLAnchorElement>(null);

  return (
    <div
      onMouseEnter={() => {
        if (!ref.current) return;
        setTooltip({
          label,
          rect: ref.current.getBoundingClientRect(),
        });
      }}
      onMouseLeave={() => {
        setTooltip({ label: '', rect: null });
      }}
    >
      <NavLink
        ref={ref}
        to={to}
        end={end}
        aria-label={label}
        className={({ isActive }) =>
          `flex flex-col items-center gap-1 h-auto w-auto items-center justify-center rounded-md transition-colors text-[9px] px-2 py-1 ${
            isActive
              ? 'bg-primary/10 text-primary'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
          }`
        }
      >
        {Icon} <span>{label}</span>
      </NavLink>
    </div>
  );
}

function Tooltip({ label, rect }: { label: string; rect: DOMRect }) {
  const offset = 8;

  const style: React.CSSProperties = {
    position: 'fixed',
    top: rect.bottom + offset,
    left: rect.left + rect.width / 2,
    transform: 'translateX(-50%)',
    zIndex: 9999,
    pointerEvents: 'none',
  };

  return (
    <div
      style={style}
      className="
        rounded px-2 py-1 text-xs font-medium
        bg-popover border border-border text-popover-foreground shadow
        whitespace-nowrap
      "
      role="tooltip"
    >
      {label}
    </div>
  );
}

interface MobileNavDrawerProps {
  open: boolean;
  onClose: () => void;
}

function MobileNavDrawer({ open, onClose }: MobileNavDrawerProps) {
  const navigate = useNavigate();

  const handleNavigate = (to: string) => {
    navigate(to);
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'sm:hidden fixed inset-0 z-45 bg-black/40 transition-opacity duration-300',
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        className={cn(
          'sm:hidden fixed top-0 left-0 z-50 h-full w-64 bg-card border-r border-border shadow-xl flex flex-col transition-transform duration-300 ease-in-out',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-border">
          <span className="text-base font-semibold !font-satoshi text-[var(--text-primary)]">
            Social Media
          </span>
          <button
            onClick={onClose}
            className="flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Close menu"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto py-2">
          <ul className="flex flex-col w-full">
            {NAV_ITEMS.map(({ to, icon, label, end }) => (
              <MobileNavItem
                key={to}
                to={to}
                icon={icon}
                label={label}
                end={end}
                onNavigate={handleNavigate}
              />
            ))}
          </ul>
        </nav>
      </div>
    </>
  );
}

interface MobileNavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  end?: boolean;
  onNavigate: (to: string) => void;
}

function MobileNavItem({ to, icon, label, end, onNavigate }: MobileNavItemProps) {
  return (
    <NavLink to={to} end={end} className="block" aria-label={label}>
      {({ isActive }) => (
        <li className="relative flex flex-col justify-start items-start w-full px-[10px] h-auto cursor-pointer group">
          <button
            onClick={() => onNavigate(to)}
            className={cn(
              'relative flex flex-row justify-start items-center w-full h-auto mt-[5px] cursor-pointer px-[5px] py-[10px] rounded-xl',
              isActive
                ? 'bg-gray-900'
                : 'bg-transparent group-hover:bg-gray-900 group-hover:text-white',
            )}
          >
            <div className="relative flex flex-row items-center justify-start w-full h-auto rounded-xl h-[24px] cursor-pointer px-[10px]">
              <span
                className={cn(
                  'w-[18px] h-[18px] flex items-center justify-center transition-colors cursor-pointer mr-2',
                  isActive
                    ? 'text-white'
                    : 'text-[var(--text-primary)] group-hover:text-[var(--text-success)]',
                )}
              >
                {icon}
              </span>
              <span
                className={cn(
                  'text-sm cursor-pointer group-hover:text-white',
                  isActive ? 'text-white' : 'text-[var(--text-primary)]',
                )}
              >
                {label}
              </span>
            </div>
          </button>
        </li>
      )}
    </NavLink>
  );
}
